import React, { FC, ChangeEvent, useEffect, useCallback, useState, Fragment, useRef } from "react"
import { useQuery } from "react-query"
import { useLocation, useNavigate } from "react-router-dom"

import CampaignIcon from "@mui/icons-material/Campaign"
import IndustrialIcon from "@mui/icons-material/FactoryOutlined"
import AnalyticsIcon from "@mui/icons-material/InsertChart"
import LogoutIcon from "@mui/icons-material/Logout"
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts"
import MenuIcon from "@mui/icons-material/Menu"
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined"
import { Box, Divider, IconButton, useMediaQuery } from "@mui/material"

import {
  Typography,
  Tabs,
  Avatar,
  Button,
  RoundedTab,
  InputChangeEvent,
  NotificationUtils,
  Switch,
  Tooltip,
  InputText,
  Menu,
  MenuItem,
} from "@synapse-analytics/synapse-ui"
import { NumberParam, useQueryParam } from "use-query-params"

import { VisionAPI } from "../../../API/VisionAPI"
import LargeScreenLogo from "../../../assets/LogoWithoutSynapse.svg?react"
import ArrowAdornment from "../../../components/ArrowAdornment"
import { routes } from "../../../routes/routes"
import { useBranchesStore, storeApi } from "../../../store"
import { Persona } from "../../../types/Custom/Types"
import { definitions } from "../../../types/Generated/apiTypes"
import auth from "../../../utils/auth"
import { getNameInitials } from "../../../utils/genericHelpers"
import BranchesAndCollectionsMenu from "../BranchesAndCollections/BranchesAndCollectionsMenu"
import SmallScreenLogo from "../assets/AVShortLogo.svg?react"
import { getFirstAuthorizedAndActiveScreen, isMultipleBranchesMode } from "../helpers"
import NotificationsMenu from "./NotificationsMenu"

import styles from "./TopBar.module.scss"

type TabInfo = {
  label: string
  value: Persona
  icon: React.ReactElement
}

type Branch = definitions["Branch"]

interface Props {
  persona: string
  togglePersonas: (persona: Persona) => void
  availablePersonas?: {
    hasAnalytics: boolean
    hasAdmin: boolean
    hasMarketing: boolean
    hasSecurity: boolean
    hasIndustrial: boolean
  }
  handleToggleDrawer: () => void
  landingScreen?: string
}

const tabsInfo: TabInfo[] = [
  {
    label: "Analytics",
    value: "analytics",
    icon: <AnalyticsIcon />,
  },
  {
    label: "Marketing",
    value: "marketing",
    icon: <CampaignIcon />,
  },
  {
    label: "Security",
    value: "security",
    icon: <ShieldOutlinedIcon />,
  },
  {
    label: "Admin",
    value: "admin",
    icon: <ManageAccountsIcon />,
  },
  {
    label: "Industrial",
    value: "industrial",
    icon: <IndustrialIcon />,
  },
]

const TopBar: FC<Props> = ({ togglePersonas, persona, availablePersonas, handleToggleDrawer, landingScreen }) => {
  const [branchParam, setBranchParam] = useQueryParam("branch", NumberParam)
  const [selectedBranch, setSelectedBranch, selectedBranches, setSelectedBranches] = useBranchesStore((state) => [
    state.selectedBranch ?? null,
    state.setSelectedBranch,
    state.selectedBranches ?? [],
    state.setSelectedBranches,
  ])
  const [clientName] = [storeApi.getState().clientName]
  const [autocompleteValue, setAutocompleteValue] = useState<string | number>(auth?.getSelectedBranch() || "")
  const [multipleBranchesAutocompleteValue, setMultipleBranchesAutocompleteValue] = useState("")
  const [personaFilterAnchor, setPersonaFilterAnchor] = useState<null | HTMLElement>(null)
  const personaInputRef = useRef<HTMLInputElement>(null)
  const isPersonaMenuOpen = Boolean(personaFilterAnchor)
  const [branchesMenuAnchor, setBranchesMenuAnchor] = useState<null | HTMLElement>(null)
  const branchesInputRef = useRef<HTMLInputElement>(null)
  const isBranchesMenuOpen = Boolean(branchesMenuAnchor)

  const navigate = useNavigate()

  const smallScreen = useMediaQuery("(max-width:890px)")
  const xSmallScreen = useMediaQuery("(max-width:610px)")
  const xxSmallScreen = useMediaQuery("(max-width:300px)")
  const largeScreen = useMediaQuery("(min-width:1441px)")
  const isTabletOrSmaller = useMediaQuery("(max-width:768px)")

  const location = useLocation()

  const userName = auth?.getFullName()

  // filter out tabs depending on the available personas
  const filteredTabs = tabsInfo.filter((tab) => {
    switch (tab.value) {
      case "analytics":
        return availablePersonas?.hasAnalytics
      case "admin":
        return availablePersonas?.hasAdmin
      case "marketing":
        return availablePersonas?.hasMarketing
      case "security":
        return availablePersonas?.hasSecurity
      case "industrial":
        return availablePersonas?.hasIndustrial
      default:
        return false
    }
  })

  const userId = auth.getUserId()

  const handleSelectBranch = useCallback(
    (branch: number) => {
      setSelectedBranch(branch)
      setBranchParam(branch)
    },
    [setBranchParam, setSelectedBranch]
  )

  const handleChangeBranch = (branch: number) => {
    handleSelectBranch(branch)
    const urlSegments = window.location.pathname.split("/").filter((segment) => segment !== "") // Split URL by '/' and remove empty segments
    if (urlSegments.length > 2) {
      // If there are more than 2 segments, navigate to home
      navigate(`/${routes?.home}`, { replace: true })
    }
  }

  const handleBranchParam = useCallback(
    (data: Branch[], branchParam: number) => {
      // Check if the branchParam exists in the fetched branches
      const branchExists = data?.some((branch) => branch.id === branchParam)

      // If the branch exists, select it; otherwise, navigate to 404 route
      if (branchExists) {
        handleSelectBranch(+branchParam)
      } else {
        navigate(`/${routes?.[404]}`)
      }
    },
    [handleSelectBranch, navigate]
  )

  function handleSuccess(data: Branch[]) {
    // If no branches are found, show a warning notification and exit
    if (!data || data.length === 0) {
      NotificationUtils.toast("No branches found for this user! Please contact admin.", {
        severity: "warning",
      })
      return
    }

    // If a branchParam exists, handle branch selection based on it
    if (branchParam) {
      handleBranchParam(data, branchParam)
      return
    }
    if (!selectedBranch) {
      handleSelectBranch(data[0].id!)
    }
  }

  const { data: branches, isLoading: areBranchesLoading } = useQuery<Branch[]>(
    ["fetchUserBranches", userId],
    ({ queryKey }) => VisionAPI?.fetchUserBranches({ user_id: queryKey[1] as string }),
    {
      onSuccess: handleSuccess,
      enabled: !!userId && !!landingScreen && location.pathname !== "/",
    }
  )

  // branches mapped to a form SUI Select optionsWithValues accept
  const mappedBranches =
    branches?.map((branch) => {
      return {
        label: branch.name,
        value: branch.id!,
      }
    }) || []

  // Check if current route is a multiple branches route (starts with "branches-")
  const isMultipleBranchesRoute = isMultipleBranchesMode(location.pathname)

  const handleSwitchMultipleBranches = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const firstScreen = getFirstAuthorizedAndActiveScreen({ persona: "analytics", mode: "multiple" })
      navigate(firstScreen)
    } else {
      // Navigate to first authorized screen in single mode for current persona
      const firstScreen = getFirstAuthorizedAndActiveScreen({ persona: persona as Persona })
      navigate(firstScreen)
    }
  }

  /**
   * Syncs selected branch with the branch URL param when it or the branches list changes (e.g. deep link with ?branch=).
   */
  useEffect(() => {
    if (branches && branchParam) handleBranchParam(branches, branchParam as number)
  }, [branchParam, branches, handleBranchParam])

  const handleChangeAutocomplete = (event: InputChangeEvent) => {
    const target = event.target
    if (Number(target.value) && !!target.label) handleChangeBranch(target.value as number)
    else {
      setAutocompleteValue(target.value)
    }
  }

  /**
   * Keeps the branch autocomplete value in sync with the selected branch when branches or selection changes.
   */
  useEffect(() => {
    if (!!selectedBranch)
      setAutocompleteValue(branches?.find((branch) => (branch?.id as number) === selectedBranch)?.id as number)
  }, [branches, selectedBranch])

  const selectedTab = filteredTabs.find((tab) => tab.value === persona)

  const handleClosePersonaMenu = (): void => {
    setPersonaFilterAnchor(null)
  }

  const handlePersonaAdornmentClick = (): void => {
    if (personaInputRef?.current) {
      if (isPersonaMenuOpen) {
        handleClosePersonaMenu()
      } else {
        setPersonaFilterAnchor(personaInputRef.current)
        personaInputRef?.current.focus()
      }
    }
  }

  const handlePersonaChange = (personaValue: Persona) => {
    togglePersonas(personaValue)
    handleClosePersonaMenu()
  }

  const handleCloseBranchesMenu = (): void => {
    setBranchesMenuAnchor(null)
  }

  const handleBranchesAdornmentClick = (): void => {
    if (branchesInputRef?.current) {
      if (isBranchesMenuOpen) {
        handleCloseBranchesMenu()
      } else {
        setBranchesMenuAnchor(branchesInputRef.current)
        branchesInputRef?.current.focus()
      }
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.barSection}>
        <Box sx={{ display: { lg: "none", xs: "block" } }}>
          <IconButton className={styles.menuButton} onClick={handleToggleDrawer} size={largeScreen ? "large" : "small"}>
            <MenuIcon />
          </IconButton>
        </Box>

        {!xxSmallScreen &&
          (largeScreen ? (
            <LargeScreenLogo className={styles.logo} onClick={() => navigate(`/${routes?.home}`)} />
          ) : (
            <SmallScreenLogo
              className={`${styles.logo} ${styles.shortLogo}`}
              onClick={() => navigate(`/${routes?.home}`)}
            />
          ))}

        {!smallScreen && !xxSmallScreen && <Divider orientation="vertical" flexItem className={styles.divider} />}
        <Tooltip title={clientName} placement="bottom">
          <Typography variant="a" textTransform="capitalize" variantColor={2} className={styles.clientName} noWrap>
            {clientName?.length > 12 ? getNameInitials(clientName) : clientName}
          </Typography>
        </Tooltip>

        {!smallScreen && auth.hasFeaturePermission("multiple_branches") && (
          <Fragment>
            <Switch checked={isMultipleBranchesRoute} onChange={handleSwitchMultipleBranches} />
            <Typography variant="p" noWrap className={styles.switchTitle}>
              Multiple Branches
            </Typography>
          </Fragment>
        )}

        <InputText
          width={xxSmallScreen ? 120 : isMultipleBranchesRoute ? 280 : 180}
          fullWidth={xSmallScreen && !xxSmallScreen}
          value={isMultipleBranchesRoute ? multipleBranchesAutocompleteValue : autocompleteValue}
          {...(!isMultipleBranchesRoute && {
            label: "Branch",
            isNotchedLabel: true,
            placeholder: "Branch",
            menuProps: {
              menuMaxContent: !largeScreen,
            },
            handleChange: handleChangeAutocomplete,
          })}
          {...(isMultipleBranchesRoute && {
            handleChange: (event: InputChangeEvent) =>
              setMultipleBranchesAutocompleteValue(event.target.value as string),
            multipleModeProps: {
              isActive: true,
              selectedValues: selectedBranches,
              setSelectedValues: (branches: (string | number)[]) => setSelectedBranches(branches as number[]),
            },
            placeholder: "search for a branch",
          })}
          {...(isMultipleBranchesRoute && {
            endAdornment: (
              <ArrowAdornment handleAdornmentClick={handleBranchesAdornmentClick} isOpen={isBranchesMenuOpen} />
            ),
            handleInputClick: handleBranchesAdornmentClick,
            anchoringRef: branchesInputRef,
            adornmentSize: "small",
          })}
          variant="filled"
          hideDescription
          loading={areBranchesLoading}
          optionsWithValues={mappedBranches}
        />
        {isMultipleBranchesRoute && (
          <BranchesAndCollectionsMenu
            open={isBranchesMenuOpen}
            onClose={handleCloseBranchesMenu}
            anchorEl={branchesMenuAnchor}
          />
        )}

        {smallScreen && !xxSmallScreen && <Divider orientation="vertical" flexItem className={styles.divider} />}
      </div>

      <div
        className={styles.barSection}
        style={{ justifyContent: "center", visibility: isMultipleBranchesRoute ? "hidden" : "visible" }}
      >
        {!xSmallScreen ? (
          <Tabs rounded activeTab={persona}>
            {filteredTabs?.map((tab) => (
              <RoundedTab
                disabled={isMultipleBranchesRoute}
                icon={() => tab.icon}
                label={!largeScreen ? "" : tab.label}
                onClick={() => togglePersonas(tab.value as Persona)}
                value={tab.value}
                selected={persona === tab.value}
                id={tab.value}
                className={styles.tab}
              />
            ))}
          </Tabs>
        ) : (
          <div className={styles.personaSelect}>
            <InputText
              value={selectedTab?.label || ""}
              key={persona}
              variant="filled"
              isSelect
              label="Persona"
              isNotchedLabel
              placeholder="Select Persona"
              disabled={isMultipleBranchesRoute}
              endAdornment={
                <ArrowAdornment handleAdornmentClick={handlePersonaAdornmentClick} isOpen={isPersonaMenuOpen} />
              }
              handleInputClick={handlePersonaAdornmentClick}
              anchoringRef={personaInputRef}
              adornmentSize="small"
              hideDescription
              width={85}
            >
              <div className={styles.selectedPersona}>
                {selectedTab &&
                  React.cloneElement(selectedTab.icon, {
                    className: `${styles.tabIcon} ${selectedTab.value === persona ? styles.selectedTabIcon : ""}`,
                  })}
              </div>
            </InputText>
            <Menu
              open={isPersonaMenuOpen}
              onClose={handleClosePersonaMenu}
              anchorEl={personaFilterAnchor}
              menuMaxContent
              key="Personas"
            >
              {filteredTabs.map((tab) => (
                <MenuItem
                  className={styles.menuItem}
                  value={tab.value}
                  isSelected={tab.value === persona}
                  onClick={() => handlePersonaChange(tab.value as Persona)}
                >
                  {React.cloneElement(tab.icon, {
                    className: `${styles.tabIcon} ${tab.value === persona ? styles.selectedTabIcon : ""}`,
                  })}

                  {tab.label}
                </MenuItem>
              ))}
            </Menu>
          </div>
        )}
      </div>

      <div className={styles.barSection} style={{ justifyContent: "flex-end" }}>
        {!largeScreen && !xxSmallScreen && <Divider orientation="vertical" flexItem className={styles.divider} />}

        <NotificationsMenu />

        {!xSmallScreen && userName && !!userName.trim() && (
          <div className={styles.userAvatar}>
            <Avatar
              name={userName}
              isLoading={false}
              isNameInitials
              size="regular"
              isTextVisible={!isTabletOrSmaller}
            />
          </div>
        )}

        {largeScreen && <Divider orientation="vertical" flexItem className={styles.divider} />}

        <Button
          className={styles.logout}
          size="small"
          startIcon={<LogoutIcon sx={{ fontSize: "16px" }} />}
          onClick={() => auth.logOut(true, navigate)}
        >
          {largeScreen ? "Logout" : ""}
        </Button>
      </div>
    </div>
  )
}

export default TopBar
